import { useMutation } from '@tanstack/react-query'
import api from '../../config/axios-config.jsx'
import { projectMutationKeys, projectQueryKeys } from '../constants.js'
import { customQueryClient } from '../../config/query-client.jsx'

/**
 * This Mutation Copy a project with couple options.
 * @param {Object} body - The request body sent to the API.
 * @param {string} body.id - The ID of the resource.
 * @param {string} body.name - The name of the resource.
 * @param {boolean} body.categories - Whether categories should be included.
 * @param {boolean} body.scaleDefinitions - Whether scale definitions should be included.
 * @param {boolean} body.team - Whether team data should be included.
 * @param {string[]} body.teamMembers - The list of team member IDs.
 * @param {boolean} body.teamGroups - Whether team groups should be included.
 * @param {string[]} body.teamGroupIds - The list of team group IDs.
 * @param {boolean} body.excludeTeamGroups - Whether selected team groups should be excluded.
 * @param {boolean} body.includeTeamGroupMembers - Whether members from selected team groups should be included.
 * @param {boolean} body.contacts - Whether contacts should be included.
 * @param {string[]} body.contactsList - The list of contact IDs.
 * @param {boolean} body.datasets - Whether datasets should be included.
 * @param {boolean} body.templates - Whether templates should be included.
 * @param {boolean} body.fields - Whether fields should be included.
 * @param {string} [body.parentId] - The optional parent ID.
 * @param {Object} options - Additional options for the mutation.
 * @returns {Object} - The new project created (details).
 */

export const useCopyProjectMutation = (options) => {
  const handleVerifyNameIsAvailable = async (projectName) => {
    const config = {
      method: 'get',
      params: {
        name: projectName
      }
    }

    const { data } = await api('/api/projects/name', config)
    return data
  }

  const handleMutation = async ({ body }) => {
    const isProjectNameAvailable = await handleVerifyNameIsAvailable(body.name)

    if (!isProjectNameAvailable) {
      throw new Error('Project name is not available')
    }

    const config = {
      method: 'put',
      data: body
    }

    const { data } = await api(`/api/v2/projects`, config)

    return data
  }

  const invalidateRelatedQueries = () => {
    customQueryClient.invalidateQueries({
      queryKey: [projectQueryKeys.GET_PROJECT_TREE_QUERY]
    })
    customQueryClient.invalidateQueries({
      queryKey: [projectQueryKeys.GET_PROJECT_LIMIT_QUERY]
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    mutationKey: projectMutationKeys.COPY_PROJECT_MUTATION,
    retry: 0,
    ...options,
    onSuccess: (data, variables, context) => {
      // Call our standard logic
      invalidateRelatedQueries()

      // Call user callback with all original parameters
      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      // Call user callback with all original parameters
      options?.onError?.(error, variables, context)
    }
  })
}
